import { inject, ref, } from "vue";
import { fillPublicPathPattern, parsePublicPathPattern, } from "../../pathPattern.js";
import { createPublicHydrationLifecycle, } from "../../runtime/browser/index.js";
function resolveMeta(options, context) {
    return options.routeMeta?.(context);
}
function isLocalized(policy) {
    return policy !== "none";
}
function allowsUnlocalized(policy) {
    return policy === "none" || policy.unlocalized === "indexable";
}
function getDefinitionPath(definition) {
    return "pathPattern" in definition
        ? toVueRouterPath(definition.pathPattern)
        : definition.path;
}
function getLocalizedDefinitionPath(definition, localeCode) {
    const localizedPaths = "pathPattern" in definition
        ? definition.localizedPathPatterns
        : definition.localizedPaths;
    const path = localizedPaths?.[localeCode.toLowerCase()];
    if (!path)
        return getDefinitionPath(definition);
    return "pathPattern" in definition ? toVueRouterPath(path) : path;
}
function getBrowserPublicPaths(definition, target) {
    if (target === "public-ssr" || definition.delivery.kind !== "hybrid-app-entry")
        return "all";
    return definition.delivery.browserPublicPaths;
}
function resolveComponent(options, componentId) {
    const component = options.components[componentId];
    if (!component)
        throw new Error(`Missing Vue component '${componentId}'.`);
    return component;
}
const VueRouterParameterModifier = {
    "": "",
    "?": "?",
    "*": "*",
};
function toVueRouterPath(pattern) {
    const { segments } = parsePublicPathPattern(pattern);
    if (segments.length === 0)
        return "/";
    return `/${segments.map((segment) => segment.kind === "static"
        ? segment.value
        : `:${segment.name}${VueRouterParameterModifier[segment.modifier]}`).join("/")}`;
}
export function createVuePublicRoutes(options) {
    const routes = [];
    for (const definition of options.site.routerRouteDefinitions) {
        if (options.includeRoute && !options.includeRoute(definition))
            continue;
        const browserPublicPaths = getBrowserPublicPaths(definition, options.target);
        if (browserPublicPaths === "none"
            && !(options.target === "browser-public" && "browserComponentId" in definition && definition.browserComponentId))
            continue;
        const path = getDefinitionPath(definition);
        const localized = isLocalized(definition.localePolicy);
        const component = resolveComponent(options, definition.componentId);
        const name = "name" in definition ? definition.name : undefined;
        if (localized) {
            const localeGroups = new Map();
            for (const locale of options.site.locales) {
                const localizedPath = getLocalizedDefinitionPath(definition, locale.code);
                const locales = localeGroups.get(localizedPath) ?? [];
                locales.push(locale);
                localeGroups.set(localizedPath, locales);
            }
            for (const [localizedPath, locales] of localeGroups) {
                const localePath = locales.length === 1
                    ? `/${locales[0].code}`
                    : `/:locale(${locales.map((locale) => locale.code).join("|")})`;
                const context = {
                    definition,
                    localized: true,
                    localeCode: locales.length === 1 ? locales[0].code : null,
                    target: options.target,
                    usesBrowserComponent: false,
                };
                routes.push({
                    path: localizedPath === "/" ? localePath : `${localePath}${localizedPath}`,
                    name: name && locales.length === 1
                        ? `${String(name)}.${locales[0].code}`
                        : name,
                    component,
                    meta: resolveMeta(options, context),
                });
            }
        }
        if (browserPublicPaths === "all"
            && allowsUnlocalized(definition.localePolicy)
            && !(options.target === "browser-public"
                && "browserComponentId" in definition
                && definition.browserComponentId)) {
            const context = {
                definition,
                localized: false,
                localeCode: null,
                target: options.target,
                usesBrowserComponent: false,
            };
            routes.push({
                path,
                name: localized ? undefined : name,
                component,
                meta: resolveMeta(options, context),
            });
        }
        if (options.target === "browser-public"
            && "browserComponentId" in definition
            && definition.browserComponentId) {
            const context = {
                definition,
                localized: false,
                localeCode: null,
                target: options.target,
                usesBrowserComponent: true,
            };
            routes.push({
                path,
                name: localized ? undefined : name,
                component: resolveComponent(options, definition.browserComponentId),
                meta: resolveMeta(options, context),
            });
        }
    }
    if (options.includeRedirects !== false)
        for (const redirect of options.site.getRedirectAliases()) {
            const path = redirect.path.includes(":")
                ? toVueRouterPath(redirect.path)
                : redirect.path;
            routes.push({
                path,
                redirect: path.includes(":")
                    ? (to) => fillPublicPathPattern(redirect.location, to.params)
                    : redirect.location,
            });
        }
    return routes;
}
export const PublicVueRouteDataInjectionKey = Symbol("public-site-vue-route-data");
export function createVuePublicRouteDataStore(initialState) {
    const state = ref(initialState);
    return {
        state,
        setState(nextState) {
            state.value = nextState;
        },
        getData(key) {
            const currentState = state.value;
            if (currentState?.status !== "ready" && currentState?.status !== "loading")
                return undefined;
            return currentState.data?.[key];
        },
        snapshot() {
            const currentState = state.value;
            return currentState?.status === "ready"
                || currentState?.status === "not-found"
                || currentState?.status === "unavailable"
                ? currentState
                : undefined;
        },
    };
}
export function provideVuePublicRouteData(app, store) {
    app.provide(PublicVueRouteDataInjectionKey, store);
}
export function useVuePublicRouteData() {
    const store = inject(PublicVueRouteDataInjectionKey, null);
    if (!store)
        throw new Error("Missing Vue public route data provider.");
    return store;
}
export function createVuePublicHydrationBootstrap(options) {
    const lifecycle = createPublicHydrationLifecycle({
        activate: options.runtime.activate,
        onError: options.onError,
    });
    const stopRuntimeSubscription = options.runtime.subscribe((state) => {
        options.routeData.setState(state);
    });
    const stopRouteHook = options.router.afterEach((to, from) => {
        options.beforeRoute?.(to, from);
        void lifecycle.navigate(to.fullPath);
    });
    let mounted = false;
    return {
        lifecycle,
        async mount(app, root) {
            if (mounted)
                throw new Error("The Vue public application is already mounted.");
            await options.router.isReady();
            const currentRoute = options.router.currentRoute.value;
            options.beforeRoute?.(currentRoute, null);
            provideVuePublicRouteData(app, options.routeData);
            app.mount(root);
            mounted = true;
            void lifecycle.completeHydration(currentRoute.fullPath);
            return app;
        },
        dispose() {
            lifecycle.dispose();
            stopRouteHook();
            stopRuntimeSubscription();
            options.runtime.dispose?.();
        },
    };
}
//# sourceMappingURL=index.js.map